import createStandardTrackingEvents from "../utils/trackingEvents";
import { hashSha256 } from "../utils/hashAccount";

const DEFAULT_ENDPOINT =
    process.env.VUE_APP_TRACKER_ENDPOINT ||
    "https://sdkapi.rcbt-sip.com/api/events";

const DEFAULT_API_KEY =
    process.env.VUE_APP_TRACKER_API_KEY ||
    "fe0066dc3bf49a40b1aca44a1e2295e40e9d534a24409984b7b1d407f3cac8c6";
const DEFAULT_INDEX = "RCBT-SIP";
const COORDS_CACHE_KEY = "geoCoordinates";
const COORDS_CACHE_TIME_KEY = "geoCoordinatesTime";
const COORDS_CACHE_DURATION_MS = 10 * 60 * 1000;
const COORDS_WAIT_TIMEOUT_MS = 3500;
const GEO_DATA_CACHE_KEY = "geoData";
const GEO_DATA_CACHE_TIME_KEY = "geoDataTime";
const GEO_DATA_CACHE_DURATION_MS = 60 * 60 * 1000;
const SERVICE_IP_WAIT_TIMEOUT_MS = 1500;

let coordinatesWarmupPromise = null;
let geoDataWarmupPromise = null;

const isMobileDevice = () =>
    /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);

const generateEventId = () => {
    const random = Math.random().toString(36).slice(2, 10);
    return `evt-${Date.now()}-${random}`;
};

const toNumberOrDefault = (value, fallback = 0) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : fallback;
};

const getAccount = () => localStorage.getItem("id") || "Guest";

const getCachedGeoData = () => {
    try {
        const raw = localStorage.getItem(GEO_DATA_CACHE_KEY);
        if (!raw) {
            return null;
        }

        const parsed = JSON.parse(raw);
        const ipv4 = String(parsed?.IPv4 || "").trim();
        if (!ipv4) {
            return null;
        }

        const rawTime = localStorage.getItem(GEO_DATA_CACHE_TIME_KEY);
        if (rawTime) {
            const cachedTime = Number(rawTime);
            if (!Number.isFinite(cachedTime)) {
                return null;
            }

            if (Date.now() - cachedTime > GEO_DATA_CACHE_DURATION_MS) {
                return null;
            }
        }

        return {
            ...parsed,
            IPv4: ipv4,
        };
    } catch (error) {
        return null;
    }
};

const setCachedGeoData = (geoData) => {
    const ipv4 = String(geoData?.IPv4 || "").trim();
    if (!ipv4) {
        return;
    }

    try {
        localStorage.setItem(
            GEO_DATA_CACHE_KEY,
            JSON.stringify({
                IPv4: ipv4,
                country: geoData?.country || null,
                city: geoData?.city || null,
            }),
        );
        localStorage.setItem(GEO_DATA_CACHE_TIME_KEY, Date.now().toString());
    } catch (error) {
        // Ignore storage errors and continue sending analytics.
    }
};

const getCachedCoordinates = () => {
    try {
        const rawCoords = localStorage.getItem(COORDS_CACHE_KEY);
        const rawTime = localStorage.getItem(COORDS_CACHE_TIME_KEY);

        if (!rawCoords || !rawTime) {
            return null;
        }

        const cachedTime = Number(rawTime);
        if (!Number.isFinite(cachedTime)) {
            return null;
        }

        if (Date.now() - cachedTime > COORDS_CACHE_DURATION_MS) {
            return null;
        }

        const parsed = JSON.parse(rawCoords);
        const latitude = Number(parsed?.latitude);
        const longitude = Number(parsed?.longitude);

        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
            return null;
        }

        return { latitude, longitude };
    } catch (error) {
        return null;
    }
};

const setCachedCoordinates = (coords) => {
    try {
        localStorage.setItem(COORDS_CACHE_KEY, JSON.stringify(coords));
        localStorage.setItem(COORDS_CACHE_TIME_KEY, Date.now().toString());
    } catch (error) {
        // Ignore storage errors and continue sending analytics.
    }
};

const getCurrentPosition = () => {
    return new Promise((resolve) => {
        if (
            typeof navigator === "undefined" ||
            !navigator.geolocation ||
            typeof navigator.geolocation.getCurrentPosition !== "function"
        ) {
            resolve({ latitude: null, longitude: null });
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) =>
                resolve({
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                }),
            () => resolve({ latitude: null, longitude: null }),
            { enableHighAccuracy: true, timeout: 7000, maximumAge: 120000 },
        );
    });
};

const hasValidNumber = (value) => Number.isFinite(Number(value));

const hasValidCoordinates = (coords) =>
    Number.isFinite(coords?.latitude) && Number.isFinite(coords?.longitude);

const hasUsableCoordinates = (coords) =>
    hasValidCoordinates(coords) &&
    !(Number(coords.latitude) === 0 && Number(coords.longitude) === 0);

const getCoordinatesFromCustomValue = (custom = {}) => {
    const directLat = custom.locLat;
    const directLon = custom.locLon;
    const legacyLat = custom.location?.lat;
    const legacyLon = custom.location?.lon;

    const latitude = hasValidNumber(directLat)
        ? Number(directLat)
        : hasValidNumber(legacyLat)
          ? Number(legacyLat)
          : null;

    const longitude = hasValidNumber(directLon)
        ? Number(directLon)
        : hasValidNumber(legacyLon)
          ? Number(legacyLon)
          : null;

    return {
        latitude,
        longitude,
    };
};

const resolveCoordinates = (custom = {}) => {
    const fromCustom = getCoordinatesFromCustomValue(custom);

    if (hasUsableCoordinates(fromCustom)) {
        return fromCustom;
    }

    const cached = getCachedCoordinates();
    if (hasUsableCoordinates(cached)) {
        return cached;
    }

    return { latitude: null, longitude: null };
};

const refreshCoordinatesCache = async () => {
    const current = await getCurrentPosition();
    if (hasUsableCoordinates(current)) {
        setCachedCoordinates(current);
    }

    return current;
};

const wait = (ms) =>
    new Promise((resolve) => {
        setTimeout(resolve, ms);
    });

const fetchPublicIp = async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
        controller.abort();
    }, 5000);

    try {
        const response = await fetch("https://api.ipify.org?format=json", {
            method: "GET",
            signal: controller.signal,
        });

        if (!response.ok) {
            return null;
        }

        const data = await response.json();
        const ipv4 = String(data?.ip || "").trim();
        return ipv4 || null;
    } catch (error) {
        return null;
    } finally {
        clearTimeout(timeoutId);
    }
};

const refreshGeoDataCache = async () => {
    const cached = getCachedGeoData();
    if (cached?.IPv4) {
        return cached;
    }

    const ip = await fetchPublicIp();
    if (!ip) {
        return null;
    }

    const geoData = {
        IPv4: ip,
        country: null,
        city: null,
    };
    setCachedGeoData(geoData);
    return geoData;
};

const warmupCoordinates = () => {
    if (!coordinatesWarmupPromise) {
        coordinatesWarmupPromise = refreshCoordinatesCache().finally(() => {
            coordinatesWarmupPromise = null;
        });
    }

    return coordinatesWarmupPromise;
};

const warmupGeoData = () => {
    if (!geoDataWarmupPromise) {
        geoDataWarmupPromise = refreshGeoDataCache().finally(() => {
            geoDataWarmupPromise = null;
        });
    }

    return geoDataWarmupPromise;
};

const getServiceIpFromCustomValue = (custom = {}) => {
    const candidate = custom.serviceIp || custom.serviceIP;
    const value = String(candidate || "").trim();
    return value || "";
};

const buildPayload = async (event = {}) => {
    const geoData = getCachedGeoData() || {};
    const custom = event.customValue || {};
    const nowIso = new Date().toISOString();
    const normalizedCoords = getCoordinatesFromCustomValue(custom);
    const normalizedLat = toNumberOrDefault(normalizedCoords.latitude, 0);
    const normalizedLon = toNumberOrDefault(normalizedCoords.longitude, 0);
    const hashedAccount = await hashSha256(custom.account || getAccount());

    return {
        _id: event._id || generateEventId(),
        _index: event._index || DEFAULT_INDEX,
        name: event.name || "click",
        category: event.category || "app",
        customValue: {
            datetime: custom.datetime || nowIso,
            account: hashedAccount,
            serviceId: toNumberOrDefault(custom.serviceId, 0),
            serviceName:
                custom.serviceName || document.title || "Unknown Service",
            serviceType: custom.serviceType || "web",
            keyword: custom.keyword || "",
            locLat: normalizedLat,
            locLon: normalizedLon,
            serviceIp: custom.serviceIp || geoData.IPv4 || "",
            device: custom.device || (isMobileDevice() ? "mobile" : "desktop"),
            operation: custom.operation || "view",
        },
    };
};

const sendManualEvent = async (event = {}, config = {}) => {
    const endpoint = config.endpoint || DEFAULT_ENDPOINT;
    const apiKey = config.apiKey || DEFAULT_API_KEY;
    const customValue = event.customValue || {};
    const customCoords = getCoordinatesFromCustomValue(customValue);
    let coords = resolveCoordinates(customValue);
    const hasCustomCoords = hasUsableCoordinates(customCoords);
    let resolvedServiceIp = getServiceIpFromCustomValue(customValue);

    if (!resolvedServiceIp) {
        const cachedGeoData = getCachedGeoData();
        resolvedServiceIp = String(cachedGeoData?.IPv4 || "").trim();

        if (!resolvedServiceIp) {
            const geoResolved = await Promise.race([
                warmupGeoData(),
                wait(SERVICE_IP_WAIT_TIMEOUT_MS).then(() => null),
            ]);
            resolvedServiceIp = String(geoResolved?.IPv4 || "").trim();
        } else {
            warmupGeoData().catch(() => {});
        }
    }

    if (!hasCustomCoords) {
        if (!hasValidCoordinates(coords)) {
            const resolved = await Promise.race([
                warmupCoordinates(),
                wait(COORDS_WAIT_TIMEOUT_MS).then(() => null),
            ]);

            if (hasUsableCoordinates(resolved)) {
                coords = resolved;
            }
        } else {
            warmupCoordinates().catch(() => {});
        }
    }

    const payload = await buildPayload({
        ...event,
        customValue: {
            ...customValue,
            locLat: hasUsableCoordinates(customCoords)
                ? Number(customCoords.latitude)
                : coords.latitude,
            locLon: hasUsableCoordinates(customCoords)
                ? Number(customCoords.longitude)
                : coords.longitude,
            serviceIp: resolvedServiceIp,
        },
    });

    const response = await fetch(endpoint, {
        method: "POST",
        headers: {
            accept: "application/json",
            "Content-Type": "application/json",
            "x-api-key": apiKey,
        },
        body: JSON.stringify(payload),
        keepalive: true,
    });

    if (!response.ok) {
        const message = await response.text();
        throw new Error(`Tracker API ${response.status}: ${message}`);
    }

    return response.json();
};

const createClickEventFromDom = (domEvent) => {
    const target = domEvent?.target;
    const elementTag = target?.tagName?.toLowerCase() || "unknown";
    const text = (target?.textContent || "").trim();

    return {
        name: "click",
        category: "interaction",
        customValue: {
            serviceType: elementTag,
            serviceName: text || document.title || "Unknown Service",
            operation: "click",
        },
    };
};

const ManualTrackerPlugin = {
    install(app, options = {}) {
        warmupCoordinates().catch(() => {});
        warmupGeoData().catch(() => {});

        const trackerConfig = {
            endpoint: options.endpoint || DEFAULT_ENDPOINT,
            apiKey: options.apiKey || DEFAULT_API_KEY,
        };

        const $sendManualEvent = (event = {}) =>
            sendManualEvent(event, trackerConfig);

        const $trackClickEvent = (domEvent, overrides = {}) =>
            $sendManualEvent({
                ...createClickEventFromDom(domEvent),
                ...overrides,
                customValue: {
                    ...createClickEventFromDom(domEvent).customValue,
                    ...(overrides.customValue || {}),
                },
            });

        const standardEvents = createStandardTrackingEvents($sendManualEvent);
        const $trackSearchSubmit = standardEvents.trackSearchSubmit;
        const $trackProductView = standardEvents.trackProductView;
        const $trackContactSubmit = standardEvents.trackContactSubmit;

        app.config.globalProperties.$sendManualEvent = $sendManualEvent;
        app.config.globalProperties.$trackClickEvent = $trackClickEvent;
        app.config.globalProperties.$trackSearchSubmit = $trackSearchSubmit;
        app.config.globalProperties.$trackProductView = $trackProductView;
        app.config.globalProperties.$trackContactSubmit = $trackContactSubmit;
        app.config.globalProperties.$trackStandardEvents = standardEvents;

        app.provide("sendManualEvent", $sendManualEvent);
        app.provide("trackClickEvent", $trackClickEvent);
        app.provide("trackSearchSubmit", $trackSearchSubmit);
        app.provide("trackProductView", $trackProductView);
        app.provide("trackContactSubmit", $trackContactSubmit);
        app.provide("trackStandardEvents", standardEvents);

        if (options.router && typeof options.router.afterEach === "function") {
            options.router.afterEach((to) => {
                $sendManualEvent({
                    name: "page_view",
                    category: "navigation",
                    customValue: {
                        serviceName: to.name || to.path || document.title,
                        serviceType: "route",
                        operation: "view",
                    },
                }).catch((error) => {
                    if (process.env.NODE_ENV !== "production") {
                        console.error(
                            "Manual tracker page_view failed:",
                            error,
                        );
                    }
                });
            });
        }
    },
};

export { sendManualEvent, buildPayload };
export default ManualTrackerPlugin;
